import React, { FC, useState, useEffect } from 'react';
import styled from '@emotion/styled';
import Icon from '~ComponentsImproved/Icon';
import ReleasesIcon from '~Assets/icons/releases.svg';
import { TransparentButton } from '~Styles/buttons';
import useModal from '~ComponentsImproved/Modal/useModal';
import { useButton, useLogger } from '~Hooks';
import Modal from '~ComponentsImproved/Modal';
import ModalWrapper from '~ComponentsImproved/Modal/ModalWrapper';
import NewsAndUpdates, { GET_LATEST_RELEASES } from '../NewsAndUpdates';
import { useQuery } from '@apollo/client';
import { GetLatestReleases, GetLatestReleasesVariables } from '../NewsAndUpdates/__queries__';

const VIEWED_RELEASE_KEY = 'viewedReleaseId';

const ButtonWrapper = styled.div`
  position: relative;
  display: inline-flex;
`;

const UnreadBadge = styled.div`
  position: absolute;
  bottom: 2px;
  right: 2px;
  min-width: 18px;
  height: 18px;
  background-color: var(--colorUIRed);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  color: var(--colorWhite);
  pointer-events: none;
`;

/** NOTE: Only rendered below 944px width - button to open NewsAndFeatures on smaller screens */
const ReleasesButton: FC = () => {
  const logger = useLogger('gateway');
  const releasesModal = useModal();
  const [unreadCount, setUnreadCount] = useState<number>(0);

  const releasesQuery = useQuery<GetLatestReleases, GetLatestReleasesVariables>(
    GET_LATEST_RELEASES,
    {
      variables: { limit: 10 },
      fetchPolicy: 'cache-and-network',
    },
  );

  const latestReleases = releasesQuery.data?.getLatestReleases.items || [];
  const latestRelease = latestReleases[0];

  // Check for unread releases
  useEffect(() => {
    if (latestReleases.length > 0) {
      const viewedReleaseIdsStr = localStorage.getItem(VIEWED_RELEASE_KEY);
      const viewedReleaseIds = viewedReleaseIdsStr ? JSON.parse(viewedReleaseIdsStr) : [];

      const unreadReleases = latestReleases.filter(
        (release) => !viewedReleaseIds.includes(release.id),
      );
      setUnreadCount(unreadReleases.length);
    }
  }, [latestReleases]);

  // Auto-open modal if triggerModal is true on the latest release
  useEffect(() => {
    if (latestRelease?.triggerModal) {
      logger.info('Auto-opening releases modal due to triggerModal flag');
      markAsViewed();
      releasesModal.show();
    }
  }, [latestRelease?.triggerModal]);

  const markAsViewed = () => {
    if (latestReleases.length > 0) {
      const viewedReleaseIdsStr = localStorage.getItem(VIEWED_RELEASE_KEY);
      const viewedReleaseIds = viewedReleaseIdsStr ? JSON.parse(viewedReleaseIdsStr) : [];
      const latestReleaseIds = latestReleases.map((release) => release.id);

      // Only keep IDs that are in the latest releases, plus add any new ones
      const cleanedViewedIds = [
        ...new Set([...viewedReleaseIds, ...latestReleaseIds]),
      ].filter((id: string) => latestReleaseIds.includes(id));

      localStorage.setItem(VIEWED_RELEASE_KEY, JSON.stringify(cleanedViewedIds));
      setUnreadCount(0);
    }
  };

  const openReleasesModalButton = useButton({
    onClick: () => {
      logger.info('Releases modal opened in Gateway');
      markAsViewed();
      releasesModal.toggle();
    },
  });

  // Don't render if no releases available
  if (latestReleases.length === 0) {
    return null;
  }

  return (
    <>
      <ButtonWrapper>
        <TransparentButton padding="none" {...openReleasesModalButton}>
          <Icon
            src={ReleasesIcon}
            width="48px"
            height="48px"
            color={unreadCount ? 'var(--colorWhite)' : 'var(--colorBlueGray200)'}
          />
        </TransparentButton>
        {unreadCount > 0 && <UnreadBadge>{unreadCount}</UnreadBadge>}
      </ButtonWrapper>

      <Modal isShowing={releasesModal.isShowing} hide={() => releasesModal.toggle()}>
        <ModalWrapper padding="small">
          <NewsAndUpdates />
        </ModalWrapper>
      </Modal>
    </>
  );
};

export default ReleasesButton;
